import React from 'react';

// three.js nie ma w projekcie typów (`declare module 'three'` w vite-env.d.ts),
// więc obiekty sceny są tu `any` — jak w podglądzie CAD.
type Three = any;

/**
 * Scena 3D „rzut jako obiekt": trzy kondygnacje rozsuwają się w aksonometrii,
 * a na górnej lądują numerowane pinezki. Jedyny animowany element graficzny
 * landingu i ekranów dostępu — pokazuje to, co produkt robi (punkt na rzucie
 * konkretnej kondygnacji), więc nie jest dekoracją.
 *
 * Zasady:
 * - `fallback` (statyczna ilustracja) renderuje się zawsze i jest pierwszą
 *   klatką: trafia do prerenderu, jest elementem LCP i zostaje, gdy WebGL
 *   zawiedzie. Scena pojawia się na niej dopiero po zbudowaniu.
 * - three.js ładuje się dynamicznie (osobny chunk, wspólny z podglądem CAD)
 *   i dopiero w bezczynności przeglądarki.
 * - Pętla renderowania stoi, gdy nic się nie rusza, gdy scena jest poza
 *   ekranem i gdy karta jest w tle.
 * - `prefers-reduced-motion`: od razu stan końcowy, bez paralaksy.
 */

export interface PlanScenePalette {
  /** Płyta kondygnacji. */
  plate: string;
  /** Ściany i krawędzie górnej kondygnacji. */
  ink: string;
  /** Ściany i krawędzie niższych kondygnacji. */
  inkSoft: string;
  /** Linie siatki rysunku na górnej płycie. */
  grid: string;
}

interface PlanSceneProps {
  /** Statyczna ilustracja — pierwsza klatka i wersja awaryjna. */
  fallback: React.ReactNode;
  /** Kolory sceny; domyślnie z tokenów aplikacji (działa też w trybie ciemnym). */
  palette?: PlanScenePalette;
  className?: string;
  /** Podpisy pinezek (`M-18 · zawilgocenie`); puste = bez podpisów. */
  labels?: string[];
  /**
   * Powiązanie z przewijaniem strony (hero): w miarę zjeżdżania bryła obraca
   * się i kondygnacje schodzą się z powrotem — jakby odkładać model.
   */
  scrollLinked?: boolean;
}

type PinSpec = { x: number; z: number; n: string; color: string };

const PINS: PinSpec[] = [
  { x: -3.0, z: -1.4, n: '18', color: '#ff5a1f' },
  { x: 2.6, z: -1.3, n: '19', color: '#d9920a' },
  { x: -1.6, z: 1.6, n: '20', color: '#14181f' },
];

// Ściany górnej kondygnacji: [x1, z1, x2, z2] w układzie płyty 10 × 6,4.
const WALLS_TOP: [number, number, number, number][] = [
  [-4.6, -2.8, 4.6, -2.8],
  [-4.6, 2.8, 4.6, 2.8],
  [-4.6, -2.8, -4.6, 2.8],
  [4.6, -2.8, 4.6, 2.8],
  [-0.9, -2.8, -0.9, 0.3],
  [-4.6, 0.3, 1.4, 0.3],
  [1.4, 0.3, 1.4, 2.8],
];
const WALLS_LOWER: [number, number, number, number][] = [
  [-4.6, -2.8, 4.6, -2.8],
  [-4.6, 2.8, 4.6, 2.8],
  [-4.6, -2.8, -4.6, 2.8],
  [4.6, -2.8, 4.6, 2.8],
  [0.6, -2.8, 0.6, 2.8],
];

const FLOORS = 3;
// Od dołu; skróty jak na przekroju budynku.
const FLOOR_TAGS = ['PARTER', '+1', '+2'];
const GAP_CLOSED = 0.16;
const GAP_OPEN = 1.85;

function readPalette(el: HTMLElement): PlanScenePalette {
  const cs = getComputedStyle(el);
  const v = (name: string, fallback: string) => cs.getPropertyValue(name).trim() || fallback;
  return {
    plate: v('--card', '#ffffff'),
    ink: v('--ink', '#14181f'),
    inkSoft: v('--muted', '#5f6673'),
    grid: v('--line', '#dfe2e6'),
  };
}

const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

export function PlanScene({ fallback, palette, className, labels = [], scrollLinked = false }: PlanSceneProps) {
  const hostRef = React.useRef<HTMLDivElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const labelRefs = React.useRef<(HTMLSpanElement | null)[]>([]);
  const floorRefs = React.useRef<(HTMLSpanElement | null)[]>([]);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    // Prerender (puppeteer) zapisuje HTML bez zawartości canvasu — scena
    // zostawiłaby w nim pustą ramkę zamiast ilustracji.
    if (navigator.webdriver) return;

    let disposed = false;
    let cleanup: (() => void) | undefined;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const colors = palette ?? readPalette(host);

    const start = async () => {
      let THREE: Three;
      try {
        THREE = await import('three');
      } catch {
        return;
      }
      if (disposed) return;

      let renderer: any;
      try {
        renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      } catch {
        return; // brak WebGL — zostaje ilustracja
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setClearColor(0x000000, 0);

      const scene = new THREE.Scene();
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 100);
      camera.position.set(8, 12.5, 10);
      camera.lookAt(0, 0.9, 0);

      scene.add(new THREE.AmbientLight(0xffffff, 2.2));
      const sun = new THREE.DirectionalLight(0xffffff, 1.4);
      sun.position.set(4, 10, 6);
      scene.add(sun);

      const rig = new THREE.Group();
      scene.add(rig);
      const disposables: { dispose: () => void }[] = [];
      const track = <T extends { dispose: () => void }>(o: T): T => {
        disposables.push(o);
        return o;
      };

      // Siatka rysunku na górnej płycie.
      const gridCanvas = document.createElement('canvas');
      gridCanvas.width = 500;
      gridCanvas.height = 320;
      const g = gridCanvas.getContext('2d');
      if (g) {
        g.fillStyle = colors.plate;
        g.fillRect(0, 0, 500, 320);
        g.strokeStyle = colors.grid;
        g.lineWidth = 1;
        for (let x = 0; x <= 500; x += 25) {
          g.beginPath();
          g.moveTo(x + 0.5, 0);
          g.lineTo(x + 0.5, 320);
          g.stroke();
        }
        for (let y = 0; y <= 320; y += 25) {
          g.beginPath();
          g.moveTo(0, y + 0.5);
          g.lineTo(500, y + 0.5);
          g.stroke();
        }
      }
      const gridTexture = track(new THREE.CanvasTexture(gridCanvas));
      gridTexture.colorSpace = THREE.SRGBColorSpace;

      const plateGeo = track(new THREE.BoxGeometry(10, 0.1, 6.4));
      const edgeGeo = track(new THREE.EdgesGeometry(plateGeo));
      const floors: any[] = [];

      for (let i = 0; i < FLOORS; i++) {
        const top = i === FLOORS - 1;
        const floor = new THREE.Group();
        const plateMat = track(
          new THREE.MeshLambertMaterial({
            color: top ? 0xffffff : new THREE.Color(colors.plate),
            map: top ? gridTexture : null,
            transparent: !top,
            opacity: top ? 1 : 0.82,
          }),
        );
        floor.add(new THREE.Mesh(plateGeo, plateMat));
        const lineMat = track(
          new THREE.LineBasicMaterial({
            color: new THREE.Color(top ? colors.ink : colors.inkSoft),
            transparent: !top,
            opacity: top ? 1 : 0.55,
          }),
        );
        floor.add(new THREE.LineSegments(edgeGeo, lineMat));

        const wallMat = track(
          new THREE.MeshLambertMaterial({
            color: new THREE.Color(top ? colors.ink : colors.inkSoft),
            transparent: !top,
            opacity: top ? 0.92 : 0.4,
          }),
        );
        const wallHeight = top ? 0.2 : 0.12;
        for (const [x1, z1, x2, z2] of top ? WALLS_TOP : WALLS_LOWER) {
          const len = Math.hypot(x2 - x1, z2 - z1);
          const geo = track(new THREE.BoxGeometry(len + 0.07, wallHeight, 0.07));
          const wall = new THREE.Mesh(geo, wallMat);
          wall.position.set((x1 + x2) / 2, 0.05 + wallHeight / 2, (z1 + z2) / 2);
          wall.rotation.y = -Math.atan2(z2 - z1, x2 - x1);
          floor.add(wall);
        }
        rig.add(floor);
        floors.push(floor);
      }

      // Pinezki: numerowany znacznik zawsze przodem do kamery (ta sama postać
      // co marker w aplikacji), nóżka i cień na płycie.
      const topFloor = floors[FLOORS - 1]!;
      const pins = PINS.map((spec) => {
        const c = document.createElement('canvas');
        c.width = 128;
        c.height = 128;
        const p = c.getContext('2d');
        if (p) {
          p.beginPath();
          p.arc(64, 64, 54, 0, Math.PI * 2);
          p.fillStyle = spec.color;
          p.fill();
          p.lineWidth = 8;
          p.strokeStyle = '#ffffff';
          p.stroke();
          p.fillStyle = '#ffffff';
          p.font = '600 50px "IBM Plex Mono", ui-monospace, monospace';
          p.textAlign = 'center';
          p.textBaseline = 'middle';
          p.fillText(spec.n, 64, 68);
        }
        const tex = track(new THREE.CanvasTexture(c));
        tex.colorSpace = THREE.SRGBColorSpace;
        const mat = track(new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0, depthTest: false }));
        const head = new THREE.Sprite(mat);
        head.scale.set(0.82, 0.82, 1);
        head.renderOrder = 10;

        const stemGeo = track(new THREE.CylinderGeometry(0.022, 0.022, 1, 8));
        const stemMat = track(new THREE.MeshBasicMaterial({ color: new THREE.Color(colors.ink), transparent: true, opacity: 0 }));
        const stem = new THREE.Mesh(stemGeo, stemMat);

        const shadowGeo = track(new THREE.CircleGeometry(0.2, 24));
        const shadowMat = track(new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0 }));
        const shadow = new THREE.Mesh(shadowGeo, shadowMat);
        shadow.rotation.x = -Math.PI / 2;
        shadow.position.set(spec.x, 0.06, spec.z);

        topFloor.add(head, stem, shadow);
        return { spec, head, mat, stem, stemMat, shadowMat, y: 2.3, v: 0, landed: false };
      });

      const REST_Y = 0.8; // wysokość środka znacznika nad płytą po wylądowaniu
      const placePin = (pin: (typeof pins)[number]) => {
        pin.head.position.set(pin.spec.x, pin.y, pin.spec.z);
        const stemLen = Math.max(pin.y - 0.34, 0.001);
        pin.stem.scale.y = stemLen;
        pin.stem.position.set(pin.spec.x, 0.05 + stemLen / 2, pin.spec.z);
      };

      const setGap = (gap: number) => {
        floors.forEach((f, i) => {
          f.position.y = i * gap;
        });
      };

      const resize = () => {
        const w = host.clientWidth;
        const h = host.clientHeight;
        if (w === 0 || h === 0) return;
        renderer.setSize(w, h, false);
        const aspect = w / h;
        // Kadr mieści rozsuniętą bryłę z zapasem na pinezki, niezależnie od proporcji.
        const viewH = Math.max(11.6, 13 / aspect);
        camera.top = viewH / 2;
        camera.bottom = -viewH / 2;
        camera.left = (-viewH * aspect) / 2;
        camera.right = (viewH * aspect) / 2;
        camera.updateProjectionMatrix();
      };

      const projected = new THREE.Vector3();
      const updateLabels = () => {
        pins.forEach((pin, i) => {
          const el = labelRefs.current[i];
          if (!el) return;
          pin.head.getWorldPosition(projected);
          projected.project(camera);
          const x = (projected.x * 0.5 + 0.5) * host.clientWidth;
          const y = (-projected.y * 0.5 + 0.5) * host.clientHeight;
          // Podpis nie może wyjść poza scenę (wąskie ekrany).
          const lx = Math.max(0, Math.min(x + 18, host.clientWidth - el.offsetWidth - 2));
          el.style.transform = `translate(${Math.round(lx)}px, ${Math.round(y - 30)}px)`;
          el.style.opacity = pin.landed ? '1' : '0';
        });
        // Podpis kondygnacji przy lewym narożniku płyty.
        floors.forEach((floor, i) => {
          const el = floorRefs.current[i];
          if (!el) return;
          projected.set(-5, 0, 3.2);
          floor.localToWorld(projected);
          projected.project(camera);
          const x = (projected.x * 0.5 + 0.5) * host.clientWidth;
          const y = (-projected.y * 0.5 + 0.5) * host.clientHeight;
          const fx = Math.max(0, x - el.offsetWidth);
          el.style.transform = `translate(${Math.round(fx)}px, ${Math.round(y - el.offsetHeight / 2)}px)`;
          el.style.opacity = '1';
        });
      };

      // Stan animacji
      const BASE_ROT_Y = -0.12;
      let t0 = performance.now();
      let last = t0;
      let raf = 0;
      let visible = true;
      const pointer = { x: 0, y: 0 };
      const rot = { x: 0, y: BASE_ROT_Y };
      // Postęp przewijania 0..1 (cel i wartość wygładzona).
      const scroll = { target: 0, value: 0 };
      const linkScroll = scrollLinked && !reduceMotion;
      const readScroll = () => {
        scroll.target = Math.max(0, Math.min(1, window.scrollY / Math.max(host.clientHeight * 1.3, 1)));
      };

      const frame = (now: number) => {
        raf = 0;
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        const t = (now - t0) / 1000;
        let moving = false;

        const smooth = 1 - Math.exp(-dt * 6);
        scroll.value += (scroll.target - scroll.value) * smooth;
        if (Math.abs(scroll.target - scroll.value) > 0.0005) moving = true;

        const gapT = reduceMotion ? 1 : Math.min(t / 0.9, 1);
        const gapOpen = GAP_OPEN * (1 - 0.55 * scroll.value);
        setGap(GAP_CLOSED + (gapOpen - GAP_CLOSED) * easeOutExpo(gapT));
        if (gapT < 1) moving = true;

        pins.forEach((pin, i) => {
          const startAt = 0.65 + i * 0.13;
          if (reduceMotion) {
            pin.y = REST_Y;
            pin.landed = true;
            pin.mat.opacity = 1;
          } else if (t >= startAt) {
            // Sprężyna z lekkim odbiciem — pinezka ma pęd, więc wolno jej raz
            // minimalnie przestrzelić; reszta sceny jest tłumiona krytycznie.
            const k = 150;
            const c = 17;
            const a = -k * (pin.y - REST_Y) - c * pin.v;
            pin.v += a * dt;
            pin.y += pin.v * dt;
            pin.mat.opacity = Math.min((t - startAt) / 0.12, 1);
            if (Math.abs(pin.y - REST_Y) < 0.002 && Math.abs(pin.v) < 0.01) {
              pin.y = REST_Y;
              pin.v = 0;
            } else {
              moving = true;
            }
            if (!pin.landed && pin.y <= REST_Y + 0.25) pin.landed = true;
          } else {
            moving = true;
          }
          pin.stemMat.opacity = pin.mat.opacity * 0.85;
          pin.shadowMat.opacity = pin.mat.opacity * Math.max(0, 0.22 - (pin.y - REST_Y) * 0.08);
          placePin(pin);
        });

        // Paralaksa: wygładzanie wykładnicze do celu (bez przestrzelenia).
        const targetY = BASE_ROT_Y + (reduceMotion ? 0 : pointer.x * 0.16) + scroll.value * 0.7;
        const targetX = (reduceMotion ? 0 : pointer.y * 0.05) + scroll.value * 0.1;
        rot.y += (targetY - rot.y) * smooth;
        rot.x += (targetX - rot.x) * smooth;
        if (Math.abs(targetY - rot.y) > 0.0005 || Math.abs(targetX - rot.x) > 0.0005) moving = true;
        rig.rotation.set(rot.x, rot.y, 0);
        rig.position.y = -(GAP_OPEN * (FLOORS - 1)) / 2 + 1.5;

        renderer.render(scene, camera);
        updateLabels();
        if (moving && visible && !document.hidden) raf = requestAnimationFrame(frame);
      };

      const kick = () => {
        if (raf || disposed || !visible || document.hidden) return;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      };

      const onPointerMove = (e: PointerEvent) => {
        const r = host.getBoundingClientRect();
        pointer.x = Math.max(-1, Math.min(1, ((e.clientX - r.left) / r.width) * 2 - 1));
        pointer.y = Math.max(-1, Math.min(1, ((e.clientY - r.top) / r.height) * 2 - 1));
        kick();
      };
      const onPointerLeave = () => {
        pointer.x = 0;
        pointer.y = 0;
        kick();
      };
      const onVisibility = () => kick();
      const onScroll = () => {
        readScroll();
        kick();
      };

      const ro = new ResizeObserver(() => {
        resize();
        kick();
      });
      ro.observe(host);
      const io = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? true;
        kick();
      });
      io.observe(host);
      if (finePointer && !reduceMotion) {
        host.addEventListener('pointermove', onPointerMove);
        host.addEventListener('pointerleave', onPointerLeave);
      }
      document.addEventListener('visibilitychange', onVisibility);
      if (linkScroll) {
        window.addEventListener('scroll', onScroll, { passive: true });
        readScroll();
        scroll.value = scroll.target;
      }

      resize();
      setGap(reduceMotion ? GAP_OPEN : GAP_CLOSED);
      pins.forEach(placePin);
      renderer.render(scene, camera);
      t0 = performance.now();
      setReady(true);
      kick();

      cleanup = () => {
        if (raf) cancelAnimationFrame(raf);
        ro.disconnect();
        io.disconnect();
        host.removeEventListener('pointermove', onPointerMove);
        host.removeEventListener('pointerleave', onPointerLeave);
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('scroll', onScroll);
        disposables.forEach((d) => d.dispose());
        renderer.dispose();
      };
      if (disposed) cleanup();
    };

    // Start w bezczynności: treść i ilustracja są na ekranie wcześniej.
    // (Safari nie ma requestIdleCallback — stąd zapasowy timeout.)
    const hasIdle = 'requestIdleCallback' in window;
    const idle = hasIdle
      ? window.requestIdleCallback(() => void start(), { timeout: 1500 })
      : window.setTimeout(() => void start(), 300);

    return () => {
      disposed = true;
      if (hasIdle) window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      cleanup?.();
    };
    // Paleta jest stała dla danego miejsca użycia; scena buduje się raz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={hostRef} className={`plan-scene ${className ?? ''}`} data-ready={ready || undefined}>
      <div className="plan-scene-fallback">{fallback}</div>
      <canvas ref={canvasRef} className="plan-scene-canvas" aria-hidden="true" />
      {FLOOR_TAGS.map((tag, i) => (
        <span
          key={tag}
          ref={(el) => {
            floorRefs.current[i] = el;
          }}
          className="plan-scene-floor"
          aria-hidden="true"
        >
          {tag}
        </span>
      ))}
      {labels.map((label, i) => (
        <span
          key={label}
          ref={(el) => {
            labelRefs.current[i] = el;
          }}
          className="plan-scene-label"
          aria-hidden="true"
        >
          {label}
        </span>
      ))}
    </div>
  );
}
